import { Inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, ReplaySubject, map, switchMap } from "rxjs";
import { LanguageApiService, LanguageModel } from "../services/api-services";
import { DOCUMENT } from "@angular/common";
import { HttpParams } from "@angular/common/http";

@Injectable()
export class LanguageService {
  public static readonly defaultLanguageCode: string = 'de-CH';
  private readonly currentLanguageCodelocalStorageKey = 'VMT.current-language-code';
  private readonly languages$ = new ReplaySubject<LanguageModel[]>(1);
  private readonly currentLanguage = new ReplaySubject<LanguageModel>(1);
  public readonly currentLanguage$ = this.currentLanguage.asObservable();

  constructor(
    private readonly translateService: TranslateService,
    private readonly languageApiService: LanguageApiService,
    @Inject(DOCUMENT) private readonly document: Document
  ) { }

  init(): Observable<any> {
    const currentLang = localStorage.getItem(this.currentLanguageCodelocalStorageKey);
    localStorage.setItem(this.currentLanguageCodelocalStorageKey, currentLang ?? LanguageService.defaultLanguageCode);
    this.translateService.setDefaultLang(LanguageService.defaultLanguageCode);

    return this.languageApiService.getAll(null)
      .pipe(switchMap(languages => {
        this.languages$.next(languages);
        this.languages$.complete();

        return this.detectLanguage()
          .pipe(switchMap(languageCode => this.setLanguageByCode(languageCode)));
      }));
  }

  public getLanguages(onlyDefaultLanguage: boolean = false): Observable<LanguageModel[]> {
    if (onlyDefaultLanguage) {
      return this.languages$.asObservable().pipe(map(items => items.filter(item => item.code === LanguageService.defaultLanguageCode)));
    }
    return this.languages$.asObservable();
  }

  public getTranslatedLanguages(onlyDefaultLanguage: boolean = false, languageCode?: string | null): Observable<LanguageModel[]> {
    languageCode = languageCode == null ? this.currentLanguageCode : languageCode;
    return this.getLanguages(onlyDefaultLanguage).pipe(map(items => {
      return items.map((item: LanguageModel) => ({
        code: item.code,
        name: item.translations.find(translation => translation.languageCode === languageCode)?.name,
        translations: item.translations
      } as LanguageModel));
    }));
  }

  private detectLanguage(): Observable<string> {
    return this.languages$.pipe(map(languages => {
      // Try to use language from URL first
      let currentLanguageCode: string | null = this.getLanguageCodeFromUrl(this.document.location.href);

      // Then try to use language from local storage 
      if(!currentLanguageCode) {
        currentLanguageCode = localStorage.getItem(this.currentLanguageCodelocalStorageKey);
      }

      // Then try to use language from browser
      if (!currentLanguageCode) {
        currentLanguageCode = this.getLanguageCodeFromBrowser(languages);
      }

      // Otherwise use default language code
      if (!currentLanguageCode) {
        currentLanguageCode = LanguageService.defaultLanguageCode;
      }

      return currentLanguageCode;
    }));
  }

  private getLanguageCodeFromUrl(url: string): string | null {
    if (url.includes('?')) {
      return new HttpParams({ fromString: url.split('?')[1] }).get('languageCode');
    }

    return null;
  }

  private getLanguageCodeFromBrowser(languages: LanguageModel[]): string | null {
    if(navigator.language) {
      const browserLanguagePrefix = navigator.language.substring(0, 2);

      return this.findLanguageCodeByTwoLetterLanguageCode(languages, browserLanguagePrefix);
    }

    return null;
  }

  public setLanguageByCode(code: string): Observable<LanguageModel> {
    return this.languages$.pipe(switchMap(languages => {
      const language = languages.find(language => language.code == code)!;

      return this.setLanguage(language)
    }));
  }

  public setLanguage(language: LanguageModel): Observable<LanguageModel> {
    return this.translateService.use(language.code)
      .pipe(map(() => {
        this.document.documentElement.lang = language.code;
        localStorage.setItem(this.currentLanguageCodelocalStorageKey, language.code);
        this.currentLanguage.next(language);

        return language;
      }));
  }

  private findLanguageCodeByTwoLetterLanguageCode(languages: LanguageModel[], prefix: string): string {
    return languages.find(language => language.code.substring(0, 2) === prefix)?.code ?? LanguageService.defaultLanguageCode;
  }

  get currentLanguageCode(): string {
    return this.translateService.currentLang;
  }
}
